import React, { useMemo, useState } from 'react'

import * as yup from 'yup'

import CasesService from '../../../services/CasesService'
import { CAPABILITIES, Permissions } from '../../../utils/Permissions'
import validation from '../../../validation'
import AdjusterSelecter from '../../AdjusterSelecter'
import MagicWand from '../../common/MagicWand'
import SmartAddressInput from '../../common/SmartAddressInput'
import CompanySummary from '../../CompanySummary'
import DatePicker from '../../controls/DatePicker'
import PhoneInput from '../../controls/PhoneInput'
import LanguageSelector from '../../LanguageSelector'
import ReferralSourceSelector from '../../ReferralSourceSelecter'

import 'react-datepicker/dist/react-datepicker.css'

export default {
  name: 'Referral',
  group: 'Claimant',
  sectionNumber: 1,
  validationSchema: getValidationSchema(),
  Component,
}

function getValidationSchema() {
  return validation.object({
    referral: validation
      .object({
        date: validation.date('Date of Referral').required(),
        dueDate: validation.date('Due Date'),
        sourceId: yup.number().label('Referral Source').nullable(),
        adjusterId: yup.number().label('Adjuster').nullable().required('Adjuster is required'),
      })
      .nullable(),
    client: validation
      .object({
        firstName: validation.name('Claimant first name').required(),
        lastName: validation.name('Claimant last name').required(),
        address: validation.address('Claimant address', false),
        phone: validation.phone('Claimant phone'),
        fax: validation.phone('Claimant mobile'),
        email: validation.email('Claimant email'),
        birthdate: validation.birthdate('Claimant date of birth'),
        alternateContact: validation.object({
          firstName: validation.name('Alternate contact first name'),
          lastName: validation.name('Alternate contact last name'),
          phone: validation.phone('Alternate contact phone'),
          fax: validation.fax('Alternate contact fax'),
          email: validation.email('Alternate contact email'),
        }),
        firstLanguage: validation.shortText('Claimant first language'),
        secondLanguage: validation.shortText('Claimant second language'),
      })
      .nullable(),
    employer: validation
      .object({
        name: validation.name('Employer name'),
        dateOfHire: validation.date('Date of hire'),
        occupation: validation.shortText('Occupation'),
        address: validation.address('Employer address'),
        contact: validation.object({
          title: validation.shortText('Employer contact title'),
          firstName: validation.name('Employer contact first name'),
          lastName: validation.name('Employer contact last name'),
          phone: validation.phone('Employer contact phone'),
          fax: validation.fax('Employer contact fax'),
          email: validation.email('Employer contact email'),
        }),
      })
      .nullable(),
  })
}

// Helper component to auto-detect required state
const FieldLabel = ({ name, label, schema, className = '', ...props }) => {
  const isRequired = useMemo(() => {
    try {
      if (!schema) return false
      const field = yup.reach(schema, name)
      // various ways yup stores this depending on version
      return (
        field?.spec?.optional === false ||
        field?.exclusiveTests?.required ||
        field?.describe().tests.some(t => t.name === 'required')
      )
    } catch (e) {
      return false
    }
  }, [schema, name])

  return (
    <label className={`form-label ${className}`} htmlFor={name} {...props}>
      {label}
      {isRequired && '*'}
    </label>
  )
}

function Component({
  caseModel,
  referralSources,
  onAddNewReferralSource,
  onEditReferralSource,
  adjusters,
  onAddNewAdjuster,
  onEditAdjuster,
  createInputProps,
  getValue,
  setModelValue,
  setModelValues,
  ErrorMessage,
  user,
}) {
  const adjuster = adjusters.find(x => x.id === caseModel.referral?.adjusterId)
  const referralSource = referralSources.find(x => x.id === caseModel.referral?.sourceId)
  const canEditReferrals = Permissions.can(user, CAPABILITIES.EDIT_REFERRAL_SOURCES)
  const canCreateReferrals = Permissions.can(user, CAPABILITIES.CREATE_REFERRAL_SOURCES)

  const schema = useMemo(() => getValidationSchema(), [])

  // ---- Smart Paste ✨ -------------------------------------------------------
  const [isExtracting, setIsExtracting] = useState(false)
  const [smartPasteToast, setSmartPasteToast] = useState(null)

  const showToast = (message, type = 'success') => {
    setSmartPasteToast({ message, type })
    setTimeout(() => setSmartPasteToast(null), 5000)
  }

  const applySmartPasteResult = result => {
    const extracted = []

    if (result.referralDate) {
      // API returns MM/DD/YYYY; referral.date is stored as YYYY-MM-DD
      const parts = result.referralDate.split('/')
      if (parts.length === 3) {
        setModelValue('referral.date', `${parts[2]}-${parts[0]}-${parts[1]}`)
        extracted.push('Date of Referral')
      }
    }
    if (result.claimNumber) {
      setModelValue('referral.claimNumber', result.claimNumber)
      extracted.push('Claim #')
    }
    if (result.policyNumber) {
      setModelValue('referral.policyNumber', result.policyNumber)
      extracted.push('Policy #')
    }
    if (result.dueDate) {
      const parts = result.dueDate.split('/')
      if (parts.length === 3) {
        setModelValue('referral.dueDate', `${parts[2]}-${parts[0]}-${parts[1]}`)
        extracted.push('Due Date')
      }
    }
    if (result.clientFirstName) {
      setModelValue('client.firstName', result.clientFirstName)
      extracted.push('First Name')
    }
    if (result.clientLastName) {
      setModelValue('client.lastName', result.clientLastName)
      extracted.push('Last Name')
    }
    if (result.employerDateOfHire) {
      const parts = result.employerDateOfHire.split('/')
      if (parts.length === 3) {
        setModelValue('employer.dateOfHire', `${parts[2]}-${parts[0]}-${parts[1]}`)
        extracted.push('Date of Hire')
      }
    }
    if (result.employerOccupation) {
      setModelValue('employer.occupation', result.employerOccupation)
      extracted.push('Occupation')
    }

    if (result.adjusterName) {
      const normalize = s => s.toLowerCase().replace(/[^a-z0-9]/g, '')
      const key = normalize(result.adjusterName)
      const match = adjusters.find(a => normalize(a.name) === key)
      if (match) {
        setModelValues([
          ['referral.adjusterId', match.id],
          ...(match.referralSourceId ? [['referral.sourceId', match.referralSourceId]] : []),
        ])
        extracted.push('Adjuster')
      } else {
        if (result.adjusterEmail) {
          const domain = result.adjusterEmail.split('@')[1]?.split('.')[0]?.toLowerCase()
          if (domain) {
            const sourceMatch = referralSources.find(
              s => normalize(s.name).includes(domain) || normalize(s.email ?? '').includes(domain),
            )
            if (sourceMatch) setModelValue('referral.sourceId', sourceMatch.id)
          }
        }
        extracted.push(`Adjuster "${result.adjusterName}" (not in library—add manually)`)
      }
    }

    return extracted
  }

  const handleSmartPasteClick = async () => {
    let text
    try {
      text = await navigator.clipboard.readText()
    } catch {
      showToast('Could not read clipboard. Copy the referral email first.', 'error')
      return
    }
    if (!text.trim()) {
      showToast('Clipboard has no text. Copy the referral email first.', 'warning')
      return
    }

    setIsExtracting(true)
    try {
      const result = await CasesService.smartPaste(text)
      const fields = applySmartPasteResult(result)
      showToast(
        fields.length > 0 ? `Pasted: ${fields.join(', ')}` : 'No fields recognized.',
        fields.length > 0 ? 'success' : 'warning',
      )
    } catch {
      showToast('Extraction failed. Please try again.', 'error')
    } finally {
      setIsExtracting(false)
    }
  }
  // --------------------------------------------------------------------------

  const filteredAdjusters = adjusters.filter(x => {
    if (caseModel.referral?.sourceId) {
      return x.referralSourceId === caseModel.referral.sourceId
    }
    return x.referralSourceId === null
  })

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between pb-3">
        <h4 className="mb-0">Referral</h4>
        <div className="d-flex align-items-center">
          {smartPasteToast && (
            <small
              className={`mr-3 ${
                smartPasteToast.type === 'success'
                  ? 'text-success'
                  : smartPasteToast.type === 'error'
                  ? 'text-danger'
                  : 'text-warning'
              }`}
            >
              {smartPasteToast.message}
            </small>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center"
            onClick={handleSmartPasteClick}
            disabled={isExtracting}
            title="Copy a referral email to clipboard, then click to extract fields"
          >
            {isExtracting ? (
              <>
                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
                <span>Reading…</span>
              </>
            ) : (
              <>
                <MagicWand active={false} />
                <span className="ml-2">Smart Paste</span>
              </>
            )}
          </button>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-3" name="referral.date" label="Date of Referral" schema={schema} />
            <DatePicker
              id="referral.date"
              {...createInputProps('referral.date')}
              dataFormat="YYYY-MM-DD"
              dateFormat="MM/dd/yyyy"
              autoComplete="off"
            />
            <ErrorMessage name="referral.date" />
          </div>
        </div>
        <div className="col-md-5">
          <div className="form-group">
            <FieldLabel className="mt-3" name="referral.referralType" label="Type of Referral" schema={schema} />
            <select id="referral.referralType" {...createInputProps('referral.referralType')} className="form-control">
              <option value="">-- Select Type --</option>
              <option value="FCM">FCM (Field Case Management)</option>
              <option value="TCM">TCM (Telephonic Case Management)</option>
              <option value="Task">Task</option>
            </select>
            <ErrorMessage name="referral.referralType" />
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-7">
          <div className="form-group">
            <FieldLabel className="mt-2" name="referral.sourceId" label="Company" schema={schema} />
            <React.Fragment>
              <ReferralSourceSelector
                {...createInputProps('referral.sourceId')}
                onChange={e => {
                  const val = e.target.value
                  setModelValues([
                    ['referral.sourceId', val],
                    ['referral.adjusterId', null],
                  ])
                }}
                onEditRequested={onEditReferralSource}
                referralSources={referralSources}
                onAddRequested={canCreateReferrals ? onAddNewReferralSource : undefined}
                isAdmin={canEditReferrals}
                isClearable={true}
              />
              <ErrorMessage name="referral.sourceId" />
            </React.Fragment>
            <small className="form-text text-muted">
              Leave blank to assign an independent (unaffiliated) adjuster.
            </small>
            <CompanySummary company={referralSource} />
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-7">
          <div className="form-group">
            <FieldLabel className="mt-2" name="referral.adjusterId" label="Adjuster" schema={schema} />
            <AdjusterSelecter
              {...createInputProps('referral.adjusterId')}
              onEditRequested={onEditAdjuster}
              adjusters={filteredAdjusters}
              onAddRequested={canCreateReferrals ? onAddNewAdjuster : undefined}
              isAdmin={canEditReferrals}
              placeholder={
                caseModel.referral?.sourceId
                  ? `Adjusters from ${referralSource?.name ?? 'company'}…`
                  : 'Independent adjusters…'
              }
            />
            <ErrorMessage name="referral.adjusterId" />
            <CompanySummary company={adjuster} />
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-2" name="referral.claimNumber" label="Claim Number" schema={schema} />
            <input
              type="text"
              className="form-control"
              {...createInputProps('referral.claimNumber')}
              maxLength={validation.maxLengths.shortText}
            />
            <ErrorMessage name="referral.claimNumber" />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-2" name="referral.policyNumber" label="Policy Number" schema={schema} />
            <input
              type="text"
              className="form-control"
              {...createInputProps('referral.policyNumber')}
              maxLength={validation.maxLengths.shortText}
            />
            <ErrorMessage name="referral.policyNumber" />
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-2" name="referral.dueDate" label="Due Date" schema={schema} />
            <DatePicker
              id="referral.dueDate"
              {...createInputProps('referral.dueDate')}
              dataFormat="YYYY-MM-DD"
              dateFormat="MM/dd/yyyy"
              autoComplete="off"
            />
            <ErrorMessage name="referral.dueDate" />
          </div>
        </div>
      </div>

      <hr className="mt-5 mb-5" />
      <h4 className="form-heading">Claimant</h4>
      <div>
        <FieldLabel className="mt-3" name="client.firstName" label="Full Name" schema={schema} />
        <div className="form-row">
          <div className="col-md-3">
            <div className="form-group">
              <input
                type="text"
                {...createInputProps('client.firstName')}
                className="form-control"
                placeholder="First Name"
                maxLength={validation.maxLengths.name}
              />
              <ErrorMessage name="client.firstName" />
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <input
                type="text"
                {...createInputProps('client.lastName')}
                className="form-control"
                placeholder="Last Name"
                maxLength={validation.maxLengths.name}
              />
              <ErrorMessage name="client.lastName" />
            </div>
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-12">
          {/* Address object itself is rarely required, fields inside it are. Custom label here? */}
          <h5 className="mt-3 mb-1">Address</h5>
          <SmartAddressInput
            value={{
              street: caseModel.client?.address?.line1 ?? '',
              city: caseModel.client?.address?.city ?? '',
              state: caseModel.client?.address?.state ?? '',
              zip: caseModel.client?.address?.zip ?? '',
            }}
            onChange={newAddr => {
              setModelValue('client.address.line1', newAddr.street)
              setModelValue('client.address.city', newAddr.city)
              setModelValue('client.address.state', newAddr.state)
              setModelValue('client.address.zip', newAddr.zip)
            }}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-2" name="client.phone" label="Phone" schema={schema} />
            <PhoneInput {...createInputProps('client.phone')} className="form-control" placeholder="(123) 456-7890" />
            <ErrorMessage name="client.phone" />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-2" name="client.fax" label="Mobile" schema={schema} />
            <PhoneInput {...createInputProps('client.fax')} className="form-control" placeholder="(123) 456-7890" />
            <ErrorMessage name="client.fax" />
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-6">
          <div className="form-group">
            <FieldLabel className="mt-2" name="client.email" label="Email" schema={schema} />
            <input
              type="email"
              {...createInputProps('client.email')}
              className="form-control"
              placeholder="name@email.com"
              maxLength={validation.maxLengths.email}
            />
            <ErrorMessage name="client.email" />
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-4">
          <div className="form-group">
            <FieldLabel name="client.birthdate" label="Date of Birth" schema={schema} />
            <React.Fragment>
              <DatePicker id="client.birthdate" {...createInputProps('client.birthdate')} dataFormat="MM/DD/YYYY" />
              <ErrorMessage name="client.birthdate" />
            </React.Fragment>
          </div>
        </div>
      </div>
      <div className="form-row">
        <h5 className="mt-2 mr-3 ml-1">Alternate Contact</h5>
        <label className="custom-switch ToggleField-component">
          <input
            type="checkbox"
            id="alternateContactSwitch"
            className="custom-control-input"
            checked={getValue('client.hasAlternateContact', false)}
            {...createInputProps('client.hasAlternateContact')}
          />
          <span
            className="custom-control-label d-inline-block"
            {
              ...{
                /* needs to not be a label because eslint can't handle it */
              }
            }
          />
        </label>
      </div>
      {getValue('client.hasAlternateContact') && (
        <React.Fragment>
          <div>
            <FieldLabel className="mt-3" name="client.alternateContact.firstName" label="Full Name" schema={schema} />
            <div className="form-row">
              <div className="col-md-3">
                <div className="form-group">
                  <input
                    {...createInputProps('client.alternateContact.firstName')}
                    className="form-control"
                    placeholder="First Name"
                    maxLength={validation.maxLengths.name}
                  />
                  <ErrorMessage name="client.alternateContact.firstName" />
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <input
                    {...createInputProps('client.alternateContact.lastName')}
                    className="form-control"
                    placeholder="Last Name"
                    maxLength={validation.maxLengths.address.name}
                  />
                  <ErrorMessage name="client.alternateContact.lastName" />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="col-md-3">
                <div className="form-group">
                  <FieldLabel className="mt-2" name="client.alternateContact.phone" label="Phone" schema={schema} />
                  <PhoneInput
                    {...createInputProps('client.alternateContact.phone')}
                    className="form-control"
                    placeholder="(123) 456-7890"
                  />
                  <ErrorMessage name="client.alternateContact.phone" />
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <FieldLabel className="mt-2" name="client.alternateContact.fax" label="Fax" schema={schema} />
                  <PhoneInput
                    {...createInputProps('client.alternateContact.fax')}
                    className="form-control"
                    placeholder="(123) 456-7890"
                  />
                  <ErrorMessage name="client.alternateContact.fax" />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="col-md-6">
                <FieldLabel className="mt-2" name="client.alternateContact.email" label="Email" schema={schema} />
                <input
                  {...createInputProps('client.alternateContact.email')}
                  className="form-control"
                  type="email"
                  placeholder="name@email.com"
                  maxLength={validation.maxLengths.email}
                />
                <ErrorMessage name="client.alternateContact.email" />
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
      <h4 className="form-heading mt-5">Employer Information</h4>
      <div className="form-row">
        <div className="col-md-6">
          <div className="form-group">
            <FieldLabel className="mt-3" name="employer.name" label="Employer Name" schema={schema} />
            <input
              type="text"
              {...createInputProps('employer.name')}
              className="form-control"
              maxLength={validation.maxLengths.name}
            />
            <ErrorMessage name="employer.name" />
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-2" name="employer.dateOfHire" label="Date of Hire" schema={schema} />
            <DatePicker
              id="employer.dateOfHire"
              {...createInputProps('employer.dateOfHire')}
              dataFormat="YYYY-MM-DD"
              dateFormat="MM/dd/yyyy"
              autoComplete="off"
            />
            <ErrorMessage name="employer.dateOfHire" />
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <FieldLabel className="mt-2" name="employer.occupation" label="Job Title" schema={schema} />
            <input
              type="text"
              {...createInputProps('employer.occupation')}
              className="form-control"
              maxLength={validation.maxLengths.shortText}
            />
            <ErrorMessage name="employer.occupation" />
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-12">
          <h5 className="mt-3 mb-1">Address</h5>
          <SmartAddressInput
            value={{
              street: caseModel.employer?.address?.line1 ?? '',
              city: caseModel.employer?.address?.city ?? '',
              state: caseModel.employer?.address?.state ?? '',
              zip: caseModel.employer?.address?.zip ?? '',
            }}
            onChange={newAddr => {
              setModelValue('employer.address.line1', newAddr.street)
              setModelValue('employer.address.city', newAddr.city)
              setModelValue('employer.address.state', newAddr.state)
              setModelValue('employer.address.zip', newAddr.zip)
            }}
          />
        </div>
      </div>
      <div>
        <FieldLabel className="mt-3" name="employer.contact.firstName" label="Contact Name" schema={schema} />
        <div className="form-row">
          <div className="col-md-3">
            <div className="form-group">
              <input
                {...createInputProps('employer.contact.firstName')}
                className="form-control"
                placeholder="First Name"
                name="employer.contact.firstName"
                maxLength={validation.maxLengths.name}
              />
              <ErrorMessage name="employer.contact.firstName" />
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <input
                {...createInputProps('employer.contact.lastName')}
                className="form-control"
                placeholder="Last Name"
                name="employer.contact.lastName"
                maxLength={validation.maxLengths.name}
              />
              <ErrorMessage name="employer.contact.lastName" />
            </div>
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-6">
          <div className="form-group">
            <FieldLabel className="mt-2" name="employer.contact.title" label="Contact Title" schema={schema} />
            <input
              {...createInputProps('employer.contact.title')}
              className="form-control"
              maxLength={validation.maxLengths.name}
            />
            <ErrorMessage name="employer.contact.title" />
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-2" name="employer.contact.phone" label="Phone" schema={schema} />
            <PhoneInput
              {...createInputProps('employer.contact.phone')}
              className="form-control"
              placeholder="(123) 456-7890"
            />
            <ErrorMessage name="employer.contact.phone" />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-2" name="employer.contact.fax" label="Fax" schema={schema} />
            <PhoneInput
              {...createInputProps('employer.contact.fax')}
              className="form-control"
              placeholder="(123) 456-7890"
            />
            <ErrorMessage name="employer.contact.fax" />
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-6">
          <div className="form-group">
            <FieldLabel className="mt-2" name="employer.contact.email" label="Email" schema={schema} />
            <input
              type="email"
              {...createInputProps('employer.contact.email')}
              className="form-control"
              placeholder="name@email.com"
              maxLength={validation.maxLengths.email}
            />
            <ErrorMessage name="employer.contact.email" />
          </div>
        </div>
      </div>
      <h4 className="form-heading mt-5">Language(s)</h4>
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-2" name="client.firstLanguage" label="Primary" schema={schema} />
            <React.Fragment>
              <LanguageSelector
                {...createInputProps('client.firstLanguage')}
                maxLength={validation.maxLengths.shortText}
              />
              <ErrorMessage name="client.firstLanguage" />
            </React.Fragment>
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="col-md-3">
          <div className="form-group">
            <FieldLabel className="mt-2" name="client.secondLanguage" label="Secondary" schema={schema} />
            <React.Fragment>
              <LanguageSelector
                {...createInputProps('client.secondLanguage')}
                maxLength={validation.maxLengths.shortText}
              />
              <ErrorMessage name="client.secondLanguage" />
            </React.Fragment>
          </div>
        </div>
      </div>
    </div>
  )
}
